import React, { useEffect, useMemo, useState } from 'react';
import * as echarts from 'echarts';
import worldJson from '../components/world.json';
import SupplierBenchmark from '../components/metrics/tabs/SupplierBenchmark';
import Pricing from '../components/metrics/tabs/Pricing';
import { ChartModal, KpiCard, ChartCard } from '../components/metrics';
import { ViewMode, ChartOption } from '../components/metrics/types';
import { useMetrics } from '../services/api/domains/metrics';
//import { mock_data } from './mock';

// Register world map for ECharts
echarts.registerMap(
  'world',
  worldJson as Parameters<typeof echarts.registerMap>[1]
);

const LGM_ORANGE = '#F15A29';
const LGM_PURPLE = '#662D91';
const GREY = '#4A4A4A';

/* ---------------- Dashboard ---------------- */
const DashboardMetrics: React.FC = () => {
  const [activeTab, setActiveTab] = useState(
    decodeURIComponent(window.location.hash.replace('#', '')) || 'Overview'
  );
  const [viewMode, setViewMode] = useState<ViewMode>('weekly');
  const [environment] = useMemo(
    () => [process.env.REACT_APP_ENV || undefined],
    []
  );

  // Fetch metrics data from API
  const metricsQuery = useMetrics();

  // Log any errors from the metrics API
  useEffect(() => {
    if (metricsQuery.status === 'error') {
      console.error('Error fetching metrics:', metricsQuery.error);
    }
    if (metricsQuery.status === 'success') {
      console.log('Metrics data:', metricsQuery.data);
    }
  }, [metricsQuery.status, metricsQuery]);

  /* ---------------- Global Modal State ---------------- */
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalOption, setModalOption] = useState<ChartOption | null>(null);
  const [modalViewMode, setModalViewMode] = useState<ViewMode | undefined>();
  const [modalToggleHandler, setModalToggleHandler] = useState<
    ((m: ViewMode) => void) | undefined
  >();

  const closeModal = () => {
    setModalOpen(false);
    setModalTitle('');
    setModalOption(null);
    setModalViewMode(undefined);
    setModalToggleHandler(undefined);
  };

  /* Register modal handler so ChartCards (and Pricing tab) can open it */
  useEffect(() => {
    window.__dashboardModalHandler = (payload?: {
      title: string;
      option: ChartOption;
      viewMode?: ViewMode;
      onToggleViewMode?: (m: ViewMode) => void;
    }) => {
      // Allow programmatic close: window.__dashboardModalHandler(null)
      if (!payload) {
        closeModal();
        return;
      }

      const { title, option, viewMode, onToggleViewMode } = payload;

      setModalTitle(title);
      setModalOption(option);
      setModalViewMode(viewMode);
      setModalToggleHandler(() => onToggleViewMode);
      setModalOpen(true);
    };
  }, []);

  /* Deep-link sync */
  useEffect(() => {
    const onHashChange = () => {
      const hash = decodeURIComponent(window.location.hash.replace('#', ''));
      if (hash) setActiveTab(hash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#${encodeURIComponent(tab)}`);
  };

  /* ---------------- Mock Data ---------------- */
  // Merge API data with mock data for metrics not yet implemented
  const weekly =
    metricsQuery.status === 'success'
      ? metricsQuery.data.overview.trends.weekly
      : {
          labels: ['W40', 'W41', 'W42', 'W43', 'W44'],
          manufacturers: [20, 28, 31, 45, 53],
          products: [50, 68, 72, 95, 110],
        };
  const monthly =
    metricsQuery.status === 'success'
      ? metricsQuery.data.overview.trends.monthly
      : {
          labels: ['Jul', 'Aug', 'Sep', 'Oct', 'Nov'],
          manufacturers: [200, 260, 320, 370, 420],
          products: [600, 700, 800, 900, 1000],
        };

  const totals =
    metricsQuery.status === 'success'
      ? metricsQuery.data.overview.totals
      : { manufacturers: 1248, products: 9876, casVerifiedPct: 82 };

  // Commercial
  const rfqTurnaround = [72, 68, 60, 54, 42]; // hours
  const conversionLift = { before: 48, after: 63 }; // %
  const autoQualifiedPct = 54; // %
  const marginLift = [0.54, 0.65, 0.72, 0.88, 1.02]; // $M

  // Efficiency
  const validationDays = [10, 9, 8, 6, 4];
  const researchHoursSaved = [80, 120, 180, 220, 260];
  const automationPct = 76;
  const errorRate = [4.8, 4.2, 3.6, 3.1, 2.7];
  const duplicateReduction = [0, 5, 9, 14, 20];

  // Quality
  const casLinked =
    metricsQuery.status === 'success'
      ? metricsQuery.data.dataQuality.casLinkedPct
      : 82;
  const dmfLinked = 74; // Mock - external data source
  const ndcLinked = 68; // Mock - external data source
  const crossLinked = 64; // Mock - external data source

  // Compliance
  const validCreds = 88;
  const detectManual = 12;
  const detectNavigator = 5;
  const diversificationScore = 73;
  const transitionsManaged = [22, 28, 33, 41, 49];

  // Strategic
  const coveragePct = 18;
  const adoptionBU = [92, 84, 88, 76];
  const integrationScore = [80, 75, 85, 60];
  const revNavigator = [1.8, 2.1, 2.4, 2.8, 3.2];
  const hoursSaved = [800, 920, 1050, 1130, 1200];

  /* ---------------- Chart Options ---------------- */
  const trendOption = (data: typeof weekly | typeof monthly) => ({
    color: [LGM_ORANGE, LGM_PURPLE],
    tooltip: { trigger: 'axis' },
    legend: {
      data: ['Manufacturers', 'Products'],
      bottom: 0,
      textStyle: { color: GREY },
    },
    grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
    xAxis: { type: 'category', data: data.labels, axisLabel: { color: GREY } },
    yAxis: { type: 'value', axisLabel: { color: GREY } },
    series: [
      {
        name: 'Manufacturers',
        type: 'line',
        smooth: true,
        data: data.manufacturers,
        areaStyle: { color: LGM_ORANGE, opacity: 0.25 },
      },
      {
        name: 'Products',
        type: 'line',
        smooth: true,
        data: data.products,
        areaStyle: { color: LGM_PURPLE, opacity: 0.25 },
      },
    ],
  });

  const barOption = (
    labels: string[],
    data: number[],
    name: string,
    color = LGM_ORANGE,
    yFmt?: (v: number) => string
  ) => ({
    color: [color],
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'category', data: labels },
    yAxis: {
      type: 'value',
      axisLabel: { formatter: (v: number) => (yFmt ? yFmt(v) : String(v)) },
    },
    series: [
      { name, type: 'bar', data, itemStyle: { color, borderRadius: 6 } },
    ],
  });

  const groupedBarOption = (
    before: number,
    after: number,
    nameA = 'Before',
    nameB = 'After',
    yFmt?: (v: number) => string
  ) => ({
    color: [LGM_ORANGE, LGM_PURPLE],
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0, data: [nameA, nameB], textStyle: { color: GREY } },
    xAxis: { type: 'category', data: ['Metric'], axisLabel: { color: GREY } },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: GREY,
        formatter: (v: number) => (yFmt ? yFmt(v) : String(v)),
      },
    },
    series: [
      {
        name: nameA,
        type: 'bar',
        data: [before],
        itemStyle: { color: LGM_ORANGE, borderRadius: 6 },
      },
      {
        name: nameB,
        type: 'bar',
        data: [after],
        itemStyle: { color: LGM_PURPLE, borderRadius: 6 },
      },
    ],
  });

  const donutOption = (pct: number, label = 'Verified') => ({
    color: [LGM_PURPLE, '#E6E6E6'],
    series: [
      {
        type: 'pie',
        radius: ['45%', '70%'],
        label: { show: true, formatter: '{b}: {d}%' },
        data: [
          { value: pct, name: label },
          { value: 100 - pct, name: 'Unverified' },
        ],
      },
    ],
  });

  const gaugeOption = (pct: number, label = 'Percent') => ({
    series: [
      {
        type: 'gauge',
        min: 0,
        max: 100,
        progress: { show: true, width: 12, itemStyle: { color: LGM_ORANGE } },
        axisLine: { lineStyle: { width: 12 } },
        detail: { formatter: '{value}%', fontSize: 18 },
        data: [{ value: pct, name: label }],
      },
    ],
  });

  const radarOption = {
    color: [LGM_ORANGE],
    radar: {
      indicator: [
        { name: 'Freshness', max: 100 },
        { name: 'Coverage', max: 100 },
        { name: 'Completeness', max: 100 },
        { name: 'Growth Rate', max: 100 },
        { name: 'Active Records', max: 100 },
      ],
      radius: '65%',
    },
    series: [
      {
        type: 'radar',
        data: [
          {
            value: [80, 85, 82, 78, 88],
            areaStyle: { color: LGM_ORANGE, opacity: 0.35 },
          },
        ],
      },
    ],
  };

  const mapOption = {
    tooltip: {
      trigger: 'item',
      formatter: (p: { name: string; value: number }) =>
        `<div style='font-size:13px;'><strong>${p.name}</strong><br/>Products: <span style='color:${LGM_ORANGE};font-weight:600;'>${p.value}</span></div>`,
    },
    visualMap: {
      min: 0,
      max: 4000,
      text: ['High', 'Low'],
      calculable: true,
      inRange: { color: ['#FEE5D9', LGM_ORANGE] },
    },
    series: [
      {
        type: 'map',
        map: 'world',
        data:
          metricsQuery.status === 'success'
            ? metricsQuery.data.overview.regionalDistribution.map(region => ({
                name: region.country,
                value: region.productCount,
              }))
            : [
                { name: 'United States', value: 3200 },
                { name: 'India', value: 2600 },
                { name: 'China', value: 2100 },
                { name: 'Germany', value: 850 },
              ],
      },
    ],
  };

  const tabs = [
    'Overview',
    'Commercial Impact',
    environment && environment !== 'prod' ? 'Operational Efficiency' : '',
    environment && environment !== 'prod' ? 'Data Asset Quality' : '',
    environment && environment !== 'prod' ? 'Compliance & Risk' : '',
    environment && environment !== 'prod' ? 'Strategic Value' : '',
    environment && environment !== 'prod' ? 'Boardroom Summary' : '',
    environment && environment !== 'prod' ? 'Supplier Benchmark' : '',
    environment && environment !== 'prod' ? 'Pricing' : '',
  ];

  return (
    <>
      {/* Sticky Tabs */}
      <div style={styles.stickyTabsStyle}>
        {tabs
          .filter(tab => tab !== '')
          .map(tab => (
            <button
              key={tab}
              onClick={() => handleTabClick(tab)}
              style={
                activeTab === tab
                  ? styles.tabButtonActiveStyle
                  : styles.tabButtonInactiveStyle
              }>
              {tab}
            </button>
          ))}
      </div>

      {/* Tab content */}
      <div style={styles.tabContentStyle}>
        {/* Overview */}
        {activeTab === 'Overview' && (
          <div style={styles.tabSectionStyle}>
            <div style={styles.kpiGridStyle}>
              <KpiCard
                label="Manufacturers"
                value={totals.manufacturers}
                color={LGM_ORANGE}
              />
              <KpiCard
                label="Products"
                value={totals.products}
                color={LGM_PURPLE}
              />
              <KpiCard
                label="% CAS Verified"
                value={`${totals.casVerifiedPct}%`}
                color={LGM_ORANGE}
              />
            </div>
            <div style={styles.chartGridTwoColStyle}>
              <ChartCard
                title="Manufacturers & Products Trend"
                option={trendOption(viewMode === 'weekly' ? weekly : monthly)}
                viewMode={viewMode}
                onToggleViewMode={setViewMode}
              />
              <ChartCard
                title="% Products with Verified CAS"
                option={donutOption(totals.casVerifiedPct)}
              />
            </div>
            <ChartCard title="Products by Region" option={mapOption} />
          </div>
        )}

        {/* Commercial Impact */}
        {activeTab === 'Commercial Impact' && (
          <div style={styles.tabSectionStyle}>
            <div style={styles.kpiGridStyle}>
              <KpiCard
                label="RFQ → Quote Time"
                value="↓ 42 hrs"
                color={LGM_ORANGE}
              />
              <KpiCard
                label="Quote Conversion Rate"
                value="↑ 63%"
                color={LGM_PURPLE}
              />
              {environment && environment !== 'prod' && (
                <KpiCard
                  label="Auto-Qualified RFQs"
                  value={`${autoQualifiedPct}%`}
                  color={LGM_ORANGE}
                />
              )}
            </div>
            <div style={styles.chartGridTwoColStyle}>
              <ChartCard
                title="Turnaround Time Trend (Hours)"
                option={barOption(
                  monthly.labels,
                  rfqTurnaround,
                  'Hours',
                  LGM_ORANGE,
                  v => `${v}h`
                )}
                viewMode={viewMode}
                onToggleViewMode={setViewMode}
              />
              {environment && environment !== 'prod' && (
                <ChartCard
                  title="Conversion Lift (Before vs After)"
                  option={groupedBarOption(
                    conversionLift.before,
                    conversionLift.after,
                    'Before',
                    'After',
                    v => `${v}%`
                  )}
                />
              )}
            </div>
            <div style={styles.chartGridTwoColStyle}>
              {environment && environment !== 'prod' && (
                <ChartCard
                  title="% RFQs Auto-Qualified"
                  option={donutOption(autoQualifiedPct, 'Auto-Qualified')}
                />
              )}
              {environment && environment !== 'prod' && (
                <ChartCard
                  title="Margin Lift ($M)"
                  option={barOption(
                    monthly.labels,
                    marginLift,
                    'Margin',
                    LGM_PURPLE,
                    v => `$${v}M`
                  )}
                />
              )}
            </div>
          </div>
        )}

        {/* Operational Efficiency */}
        {activeTab === 'Operational Efficiency' &&
          environment &&
          environment !== 'prod' && (
            <div style={styles.tabSectionStyle}>
              <div style={styles.kpiGridStyle}>
                <KpiCard
                  label="Validation Cycle"
                  value="↓ 60%"
                  color={LGM_ORANGE}
                />
                <KpiCard
                  label="Research Hours Saved"
                  value="↑ 80%"
                  color={LGM_PURPLE}
                />
                <KpiCard
                  label="Automation Coverage"
                  value="76%"
                  color={LGM_ORANGE}
                />
              </div>
              <div style={styles.chartGridTwoColStyle}>
                <ChartCard
                  title="Validation Cycle (days)"
                  option={barOption(
                    monthly.labels,
                    validationDays,
                    'Days',
                    LGM_ORANGE,
                    v => `${v}d`
                  )}
                />
                <ChartCard
                  title="Research Hours Saved"
                  option={barOption(
                    weekly.labels,
                    researchHoursSaved,
                    'Hours',
                    LGM_PURPLE,
                    v => `${v}h`
                  )}
                />
              </div>
              <div style={styles.chartGridThreeColStyle}>
                <ChartCard
                  title="Automation Coverage %"
                  option={gaugeOption(automationPct, 'Coverage')}
                />
                <ChartCard
                  title="Error Rate (%)"
                  option={barOption(
                    monthly.labels,
                    errorRate,
                    'Error %',
                    LGM_ORANGE,
                    v => `${v}%`
                  )}
                />
                <ChartCard
                  title="Duplicate/Conflict Reduction (%)"
                  option={barOption(
                    monthly.labels,
                    duplicateReduction,
                    'Reduction %',
                    LGM_PURPLE,
                    v => `${v}%`
                  )}
                />
              </div>
              <div style={styles.centeredChartContainerStyle}>
                <div style={styles.radarChartWrapperStyle}>
                  <ChartCard
                    title="Operational Health (Radar)"
                    option={radarOption}
                  />
                </div>
              </div>
            </div>
          )}

        {/* Data Asset Quality */}
        {activeTab === 'Data Asset Quality' &&
          environment &&
          environment !== 'prod' && (
            <div style={styles.tabSectionStyle}>
              <div style={styles.kpiGridStyle}>
                <KpiCard
                  label="APIs Catalogued"
                  value={
                    metricsQuery.status === 'success'
                      ? metricsQuery.data.dataQuality.apisCatalogued.toLocaleString()
                      : '9,876'
                  }
                  color={LGM_PURPLE}
                />
                <KpiCard
                  label="Facilities Catalogued"
                  value={
                    metricsQuery.status === 'success'
                      ? metricsQuery.data.dataQuality.facilitiesCatalogued.toLocaleString()
                      : '1,248'
                  }
                  color={LGM_ORANGE}
                />
                <KpiCard
                  label="Cross-Link Coverage"
                  value={`${crossLinked}%`}
                  color={LGM_PURPLE}
                />
              </div>
              <div style={styles.chartGridThreeColStyle}>
                <ChartCard
                  title="CAS Linked %"
                  option={barOption(
                    ['CAS'],
                    [casLinked],
                    '% Linked',
                    LGM_ORANGE,
                    v => `${v}%`
                  )}
                />
                <ChartCard
                  title="DMF Linked %"
                  option={barOption(
                    ['DMF'],
                    [dmfLinked],
                    '% Linked',
                    LGM_PURPLE,
                    v => `${v}%`
                  )}
                />
                <ChartCard
                  title="NDC Linked %"
                  option={barOption(
                    ['NDC'],
                    [ndcLinked],
                    '% Linked',
                    LGM_ORANGE,
                    v => `${v}%`
                  )}
                />
              </div>
            </div>
          )}

        {/* Compliance & Risk */}
        {activeTab === 'Compliance & Risk' &&
          environment &&
          environment !== 'prod' && (
            <div style={styles.tabSectionStyle}>
              <div style={styles.kpiGridStyle}>
                <KpiCard
                  label="Valid Credentials"
                  value={`${validCreds}%`}
                  color={LGM_ORANGE}
                />
                <KpiCard
                  label="Detection Lead Time"
                  value="↓ 58%"
                  color={LGM_PURPLE}
                />
                <KpiCard
                  label="Transitions Managed"
                  value={`${transitionsManaged.at(-1)}%`}
                  color={LGM_ORANGE}
                />
              </div>
              <div style={styles.chartGridTwoColStyle}>
                <ChartCard
                  title="Credentials Coverage"
                  option={donutOption(validCreds, 'Valid')}
                />
                <ChartCard
                  title="Detection Lead Time"
                  option={groupedBarOption(
                    detectManual,
                    detectNavigator,
                    'Manual',
                    'Navigator',
                    v => `${v}d`
                  )}
                />
              </div>
              <div style={styles.chartGridTwoColStyle}>
                <ChartCard
                  title="Supplier Diversification"
                  option={gaugeOption(diversificationScore, 'Diversification')}
                />
                <ChartCard
                  title="% Supplier Transitions Managed"
                  option={barOption(
                    monthly.labels,
                    transitionsManaged,
                    '% Managed',
                    LGM_PURPLE,
                    v => `${v}%`
                  )}
                />
              </div>
            </div>
          )}

        {/* Strategic Value */}
        {activeTab === 'Strategic Value' &&
          environment &&
          environment !== 'prod' && (
            <div style={styles.tabSectionStyle}>
              <div style={styles.kpiGridStyle}>
                <KpiCard
                  label="Market Coverage"
                  value={`${coveragePct}%`}
                  color={LGM_ORANGE}
                />
                <KpiCard
                  label="BU Adoption (Avg)"
                  value="85%"
                  color={LGM_PURPLE}
                />
                <KpiCard
                  label="Integration Points"
                  value="7"
                  color={LGM_ORANGE}
                />
              </div>
              <div style={styles.chartGridTwoColStyle}>
                <ChartCard
                  title="Market Coverage (Gauge)"
                  option={gaugeOption(coveragePct, 'Coverage')}
                />
                <ChartCard
                  title="BU Adoption & Integration by Function"
                  option={{
                    color: [LGM_ORANGE, LGM_PURPLE],
                    tooltip: { trigger: 'axis' },
                    legend: { bottom: 0, textStyle: { color: GREY } },
                    xAxis: {
                      type: 'category',
                      data: ['Sales', 'Quality', 'Regulatory', 'Supply'],
                      axisLabel: { color: GREY },
                    },
                    yAxis: { type: 'value', axisLabel: { color: GREY } },
                    series: [
                      {
                        name: 'Adoption %',
                        type: 'bar',
                        data: adoptionBU,
                        itemStyle: { color: LGM_ORANGE, borderRadius: 6 },
                      },
                      {
                        name: 'Integration Score',
                        type: 'line',
                        data: integrationScore,
                        lineStyle: { color: LGM_PURPLE },
                      },
                    ],
                  }}
                />
              </div>
              <ChartCard
                title="Revenue Trend (Navigator-Sourced, $M)"
                option={barOption(
                  monthly.labels,
                  revNavigator,
                  '$M',
                  LGM_ORANGE,
                  v => `$${v}M`
                )}
                viewMode={viewMode}
                onToggleViewMode={setViewMode}
              />
            </div>
          )}

        {/* Boardroom Summary */}
        {activeTab === 'Boardroom Summary' &&
          environment &&
          environment !== 'prod' && (
            <div style={styles.tabSectionStyle}>
              <div style={styles.kpiGridStyle}>
                <KpiCard label="Speed" value="↓ 38%" color={LGM_ORANGE} />
                <KpiCard label="Quality" value="82%" color={LGM_PURPLE} />
                <KpiCard
                  label="Scale"
                  value={
                    metricsQuery.status === 'success'
                      ? `${metricsQuery.data.boardroom.scale.products.toLocaleString()} / ${metricsQuery.data.boardroom.scale.facilities.toLocaleString()}`
                      : '9,876 / 1,248'
                  }
                  color={LGM_ORANGE}
                />
                <KpiCard
                  label="Efficiency"
                  value="1,200+ hrs"
                  color={LGM_PURPLE}
                />
                <KpiCard
                  label="Defensibility"
                  value="3+ attrs"
                  color={LGM_ORANGE}
                />
              </div>
              <div style={styles.chartGridTwoColStyle}>
                <ChartCard
                  title="Conversion Lift (Before vs After)"
                  option={groupedBarOption(
                    conversionLift.before,
                    conversionLift.after,
                    'Before',
                    'After',
                    v => `${v}%`
                  )}
                />
                <ChartCard
                  title="Market Coverage (Gauge)"
                  option={gaugeOption(coveragePct, 'Coverage')}
                />
              </div>
              <ChartCard
                title="Efficiency Gains (Hours Saved)"
                option={barOption(
                  ['Q1', 'Q2', 'Q3', 'Q4', 'Q5'],
                  hoursSaved,
                  'Hours',
                  LGM_PURPLE
                )}
              />
            </div>
          )}

        {/* Supplier Benchmark */}
        {activeTab === 'Supplier Benchmark' &&
          environment &&
          environment !== 'prod' && <SupplierBenchmark />}

        {/* Pricing */}
        {activeTab === 'Pricing' && environment && environment !== 'prod' && (
          <Pricing />
        )}
      </div>

      {/* Global Modal */}
      {modalOpen && modalOption && (
        <ChartModal
          open={modalOpen}
          title={modalTitle}
          option={modalOption}
          viewMode={modalViewMode}
          onToggleViewMode={modalToggleHandler}
          onClose={closeModal} // ✅ NEW (requires ChartModal prop update)
        />
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  // Sticky tabs
  stickyTabsStyle: {
    position: 'sticky',
    top: 0,
    zIndex: 40,
    backgroundColor: '#FAFAFA',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '12px', // gap-3
    padding: '12px 0', // py-3
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // shadow-sm
  } as React.CSSProperties,
  tabButtonActiveStyle: {
    padding: '6px 16px', // px-4 py-1.5
    borderRadius: '9999px', // rounded-full
    border: '1px solid #662D91',
    fontSize: '14px', // text-sm
    fontWeight: '500', // font-medium
    transition: 'all 300ms',
    backgroundColor: '#662D91',
    color: '#ffffff',
    cursor: 'pointer',
  },
  tabButtonInactiveStyle: {
    padding: '6px 16px', // px-4 py-1.5
    borderRadius: '9999px', // rounded-full
    border: '1px solid #d1d5db', // border-gray-300
    fontSize: '14px', // text-sm
    fontWeight: '500', // font-medium
    transition: 'all 300ms',
    backgroundColor: '#ffffff',
    color: '#4A4A4A',
    cursor: 'pointer',
  },
  // Tab content
  tabContentStyle: {
    padding: '24px', // p-6
    backgroundColor: '#FAFAFA',
  },
  tabSectionStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px', // space-y-8
  } as React.CSSProperties,
  // Grid layouts
  kpiGridStyle: {
    display: 'grid',
    gap: '24px', // gap-6
    width: '100%',
    justifyItems: 'center',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    padding: '0 16px', // px-4
  },
  chartGridTwoColStyle: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px', // gap-6
    padding: '0 16px', // px-4
  },
  chartGridThreeColStyle: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px', // gap-6
    padding: '0 16px', // px-4
  },
  centeredChartContainerStyle: {
    display: 'flex',
    justifyContent: 'center',
    padding: '0 16px', // px-4
  },
  radarChartWrapperStyle: {
    width: '100%',
    maxWidth: '600px',
  },
};

// Add media query support via JavaScript for responsive grids
if (typeof window !== 'undefined') {
  const mediaQuery = window.matchMedia('(min-width: 768px)');
  if (mediaQuery.matches) {
    styles.chartGridTwoColStyle = {
      ...styles.chartGridTwoColStyle,
      gridTemplateColumns: 'repeat(2, 1fr)',
    };
    styles.chartGridThreeColStyle = {
      ...styles.chartGridThreeColStyle,
      gridTemplateColumns: 'repeat(3, 1fr)',
    };
  }
}

export default DashboardMetrics;
